<hlm-dialog-surface size="sm">
  <hlm-dialog-header>
    <hlm-dialog-icon-badge tone="primary">
      <hlm-icon name="lucideGlobe" size="1.1rem" />
    </hlm-dialog-icon-badge>
    <div hlmDialogHeading>
      @if (data.existing) {
        <h2 hlmDialogTitle>Edit domain — {{ data.existing.domain }}</h2>
        <p hlmDialogDescription>
          Update the default brand and sub-brand for this domain.
        </p>
      } @else {
        <h2 hlmDialogTitle>Add domain</h2>
        <p hlmDialogDescription>
          Register a new global domain and assign its default brand routing.
        </p>
      }
    </div>
    <button hlmDialogClose tabindex="-1" (click)="cancel()">
      <hlm-icon name="lucideX" size="1rem" />
    </button>
  </hlm-dialog-header>

  <section hlmDialogBody>
    <form id="domainForm" [formGroup]="form" class="flex flex-col gap-5" (ngSubmit)="submit()">

      <!-- Domain field (disabled when editing) -->
      <label class="flex flex-col gap-1.5">
        <span class="cmt-eyebrow">Domain *</span>
        <input
          hlmInput
          type="text"
          formControlName="domain"
          placeholder="audelia.com"
          autocomplete="off"
        />
        @if (form.controls.domain.errors?.['domainFormat']) {
          <p class="text-sm text-destructive">
            Use a bare host like audelia.com — no http://, path, or port.
          </p>
        }
        @if (data.existing) {
          <p class="text-sm text-muted-foreground">
            Domain cannot be changed after creation.
          </p>
        }
      </label>

      <!-- MX host (optional) -->
      <label class="flex flex-col gap-1.5">
        <span class="cmt-eyebrow">MX host (optional)</span>
        <input
          hlmInput
          type="text"
          formControlName="mx"
          placeholder="mail.example.com"
          autocomplete="off"
        />
      </label>

      <!-- Default brand picker -->
      <label class="flex flex-col gap-1.5">
        <span class="cmt-eyebrow">Default brand *</span>
        <select hlmNativeSelect formControlName="defaultBrand">
          <option [ngValue]="null" disabled>— select brand —</option>
          @for (brand of data.brands; track brand.id) {
            <option [ngValue]="brand.id">{{ brand.name }}</option>
          }
        </select>
        @if (form.controls.defaultBrand.touched && form.controls.defaultBrand.invalid) {
          <p class="text-sm text-destructive">A default brand is required.</p>
        }
      </label>

      <!-- Default sub-brand picker (optional) — scoped to sub-brands of the selected default brand -->
      <label class="flex flex-col gap-1.5">
        <span class="cmt-eyebrow">Default sub-brand</span>
        <select hlmNativeSelect formControlName="defaultSubBrand">
          <option [ngValue]="null">— none —</option>
          @for (s of subBrandOptions(); track s.id) {
            <option [ngValue]="s.id">{{ s.name }}</option>
          }
        </select>
      </label>

      <!-- Linked brands -->
      <div class="flex flex-col gap-2">
        <span class="cmt-eyebrow">Linked brands</span>
        <p class="text-sm text-muted-foreground">
          Select which brands this domain is available for. The mail-domain flag is managed
          per-brand on the brand's own Domains tab.
        </p>
        <div class="flex max-h-52 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-muted/30 p-2">
          @for (brand of data.brands; track brand.id) {
            <label class="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-muted/60">
              <hlm-checkbox
                [checked]="isLinked(brand.id)"
                (checkedChange)="toggleBrand(brand.id)"
              />
              <span class="text-sm text-foreground">{{ brand.name }}</span>
            </label>
          }
          @if (data.brands.length === 0) {
            <p class="px-2 py-2 text-sm text-muted-foreground">No brands available.</p>
          }
        </div>
      </div>

      <!-- Inline error -->
      @if (errorMessage()) {
        <hlm-alert tone="error">
          <hlm-icon name="lucideCircleAlert" size="1rem" />
          {{ errorMessage() }}
        </hlm-alert>
      }

    </form>
  </section>

  <footer hlmDialogFooter>
    <button hlmBtn variant="outline" type="button" (click)="cancel()">Cancel</button>
    <button
      hlmBtn
      variant="default"
      type="submit"
      form="domainForm"
      [disabled]="saving() || !canWrite()"
    >
      @if (saving()) {
        <hlm-spinner size="16" />
        Saving…
      } @else {
        <hlm-icon name="lucideCheck" size="0.9rem" />
        @if (data.existing) { Save } @else { Add domain }
      }
    </button>
  </footer>
</hlm-dialog-surface>
